"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { z } from "zod";

import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormEmail,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormPassword,
  FormSubmitButton,
} from "@/components/ui/form";
import { Stack } from "@/components/ui/stack";

import { fetchApi } from "@/lib/api/client";
import { destinationAfterPasswordLogin } from "@/lib/auth/return-to";
import { zRequiredEmail } from "@/lib/form-schema";

const schema = z.object({
  email: zRequiredEmail,
  password: z.string().min(1, "Required"),
});

// NOTE: User will be redirected if they are already logged in. See middleware.ts.
function LoginForm({
  searchParams,
}: {
  searchParams?: ReturnType<typeof useSearchParams>;
}) {
  const [googleError, setGoogleError] = useState<string | null>(null);

  const onSubmit = async (values: z.output<typeof schema>) => {
    const response = await fetchApi("session", {
      method: "POST",
      body: JSON.stringify({
        email: values.email,
        password: values.password,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Invalid email or password");
    }

    window.location.href = destinationAfterPasswordLogin(
      searchParams?.get("returnTo"),
      "/two-factor",
    );
  };

  return (
    <Stack gap={6} className="w-full">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Sign in to your account</CardTitle>
        </CardHeader>
        <CardContent>
          <Form
            schema={schema}
            defaultValues={{ email: "", password: "" }}
            onSubmit={onSubmit}
          >
            <Stack gap={4}>
              <FormField name="email">
                <FormItem id="email">
                  <FormLabel>Email</FormLabel>
                  <FormEmail required />
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField name="password">
                <FormItem id="password">
                  <div className="flex items-center justify-between">
                    <FormLabel>Password</FormLabel>
                    <Link
                      href="/forgot-password"
                      className="text-xs text-muted-foreground hover:text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <FormPassword required autoComplete="current-password" />
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormSubmitButton className="w-full" pending="Signing in...">
                Sign in
              </FormSubmitButton>
            </Stack>
          </Form>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                OR
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <GoogleSignInButton handleError={setGoogleError} />
            {googleError && (
              <p className="text-sm text-destructive">{googleError}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="p-0">
          <div className="w-full rounded-b-xl bg-muted/50 px-6 py-4 text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link
              href="/signup"
              className="underline underline-offset-4 hover:text-primary"
            >
              Sign up
            </Link>
          </div>
        </CardFooter>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground">
        By signing in, you agree to our <br />
        <Link
          href="/terms"
          className="underline underline-offset-4 hover:text-primary"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href="/privacy"
          className="underline underline-offset-4 hover:text-primary"
        >
          Privacy Policy
        </Link>
        .
      </div>
    </Stack>
  );
}

// Reading search parameters through useSearchParams() without a Suspense boundary will
// opt the entire page into client-side rendering. This could cause your page to be blank
// until the client-side JavaScript has loaded.
// https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
function LoginFormWithSearchParams() {
  const searchParams = useSearchParams();
  return <LoginForm searchParams={searchParams} />;
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginForm />}>
      <LoginFormWithSearchParams />
    </Suspense>
  );
}
